import React, { useState } from 'react';
import { Button } from '@components/Button';
import { Flex } from '@components/Flex';
import { Columns } from '@components/Columns';
import { CTALink } from '@components/CTALink';
import { JoykitIcon } from '@components/JoykitIcon';
import { ParticleJoyLogo } from './ParticleJoyLogo';
import { GetStartedButton } from '@components/GetStartedButton/GetStartedButton';
import { getRootUrl } from '@utils/urlHelpers.util';
import {
  FooterColumns,
  FooterHeroMenuItem,
  FooterSubHeroItem,
  FooterSubHeroItemText,
  FooterSubHeroItems,
  SocialMediaMenuList,
  FooterColumn,
  CookiesWrapper,
  CTALinkHash,
  CTALinkHashMobile,
  BottomMenuList,
  BottomMenuListMobile,
  BottomText,
  BottomCTALink,
  BottomPrivacyCTALink,
} from './Footer.styles';
import { Box } from '@components/Box';
import { CookiesBanner, COOKIE_STATUS, COOKIE_STATUS_ACCEPTED, COOKIE_BANNER_CLOSED } from './CookiesBanner';
import { getLocalStorage } from '@utils/storage';

const footerPageId = ({ page }: { page?: MarcomPageName }) => {
  switch (page) {
    case 'home':
      return 'Home_Footer';
    case 'wedding-website':
      return 'Wedding_Websites_Footer';
    case 'guest-list':
      return 'Guest_List_Footer';
    case 'online-rsvp':
      return 'RSVP_Footer';
    case 'save-the-date':
      return 'Save_the_Dates_Footer';
    case 'registry':
      return 'Registry_Footer';
    case 'app':
      return 'App_Footer';
    default:
      return 'mainLayoutFooter';
  }
};

const appGetStartedMenuContent = ({
  page,
  handleGetStarted,
}: {
  page?: MarcomPageName;
  handleGetStarted?: (e: React.MouseEvent) => void;
}) => {
  return (
    <>
      <GetStartedButton id="footer2create" page={page} className="get-started-button" variant="prime" onClick={handleGetStarted}>
        Get Started
      </GetStartedButton>
      <Box marginTop={[5, 0]}>
        <Button
          id="footer2login"
          href={getRootUrl('/login')}
          forwardedAs={'a'}
          className="bottom-footer-link footer-login-button"
          variant="altPurpleSecondary"
          onClick={handleGetStarted}
        >
          Sign In
        </Button>
      </Box>
      <CTALink
        id="footer2login"
        href={getRootUrl('/login')}
        className="bottom-footer-link footer-login-link"
        variant="altPurple"
        onClick={handleGetStarted}
      >
        Sign In
      </CTALink>
      <Box marginTop={[6, 0]}>
        <CTALink id="footer2find" href={getRootUrl('/find-a-couple')} className="bottom-footer-link" variant="altPurple">
          Find a Couple
        </CTALink>
      </Box>
    </>
  );
};

const getFooterBottomMenu = () => {
  return (
    <>
      <Flex alignItems="center">
        <BottomPrivacyCTALink id="footer2Terms" href="https://withjoy.com/eula" variant="footerAlt">
          Terms
        </BottomPrivacyCTALink>
        <BottomText tagName="span" color="footerText" variant="featureHed17">
          &
        </BottomText>
        <BottomPrivacyCTALink id="footer2privacy" href="https://privacy.withjoy.com" variant="footerAlt">
          Privacy
        </BottomPrivacyCTALink>
      </Flex>
      <BottomCTALink
        id="footerPrivacyNotice"
        href="https://privacy.withjoy.com/privacy-policy#california-privacy-notice"
        variant="footerAlt"
      >
        California Privacy Notice
      </BottomCTALink>
      <BottomCTALink id="footerDonotShareNotice" href="https://privacy.withjoy.com/do-not-share" variant="footerAlt">
        Do Not Share My Personal Information
      </BottomCTALink>
    </>
  );
};

export const Footer: React.FC<{ page?: MarcomPageName; handleGetStarted?: (e: React.MouseEvent) => void }> = ({
  page,
  handleGetStarted,
}) => {
  const [isAcceptCookies, setIsAcceptCookies] = useState(getLocalStorage().getItem(COOKIE_STATUS) === COOKIE_STATUS_ACCEPTED);
  const [isClosedCookies, setIsClosedCookies] = useState(getLocalStorage().getItem(COOKIE_BANNER_CLOSED) === 'yes');
  const closeWindow = () => {
    setIsClosedCookies(true);
    getLocalStorage().setItem('cookiesBannerCosed', 'yes');
  };
  // TODO: Will remove hide cookies bar with feature flag code after test on prod.
  let showCookiesBar = false;
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const featureQueryString = urlParams.get('feature.flag.marcom'.toLowerCase());
    showCookiesBar = featureQueryString === 'cookies' ? true : false;
  }
  return (
    <footer id={footerPageId({ page })}>
      <div className="footer-menu-container">
        <FooterColumns>
          <FooterColumn desktop="half" mobile="full">
            <div className="footer-hero-menu">
              <FooterHeroMenuItem>
                <CTALink id="footer2weddingWebsite" href="/wedding-website" variant="footerAlt">
                  Wedding Website
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2registry" href="/registry" variant="footerAlt">
                  Registry
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2guestList" href="/guest-list" variant="footerAlt">
                  Guest List
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2saveTheDate" href="/save-the-date" variant="footerAlt">
                  Save the Dates
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2weddingInvitations" href="/wedding-invitations" variant="footerAlt">
                  Invitations
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2OnlineRsvp" href="/online-rsvp" variant="footerAlt">
                  Smart RSVP
                </CTALink>
              </FooterHeroMenuItem>
              <FooterHeroMenuItem>
                <CTALink id="footer2registry" href="/app" variant="footerAlt">
                  Mobile App
                </CTALink>
              </FooterHeroMenuItem>
            </div>
            <div className="footer-app-get-started-menu">{appGetStartedMenuContent({ page, handleGetStarted })}</div>
          </FooterColumn>
          <FooterColumn desktop="half" mobile="full">
            <Flex justifyContent={'flex-end'}>
              <div className="footer-sub-hero-menus-container">
                <div className="footer-sub-hero-menu">
                  <FooterSubHeroItem>
                    <FooterSubHeroItemText marginTop={[8]} tagName="h1" color="footerText" variant="featureHed20">
                      Resources
                    </FooterSubHeroItemText>
                  </FooterSubHeroItem>
                  <FooterSubHeroItems>
                    <CTALink id="footer2designs" href="/designs" variant="footerAlt">
                      Design Templates
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2inspiration" href="https://withjoy.com/blog/" variant="footerAlt">
                      Ideas & Inspiration
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2faq" href="/faq" variant="footerAlt">
                      FAQs
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2help" href="https://help.withjoy.com" variant="footerAlt">
                      Help & Support
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2pricing" href="/pricing" variant="footerAlt">
                      Pricing
                    </CTALink>
                  </FooterSubHeroItems>
                </div>
                <div className="footer-sub-hero-menu" style={{ paddingBottom: '49px' }}>
                  <FooterSubHeroItem>
                    <FooterSubHeroItemText marginTop={[8]} tagName="h1" color="footerText" variant="featureHed20">
                      About Joy
                    </FooterSubHeroItemText>
                  </FooterSubHeroItem>
                  <FooterSubHeroItems>
                    <CTALink id="footer2about" href="/our-story" variant="footerAlt">
                      Our Story
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2careers" href="/careers" variant="footerAlt">
                      Careers
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2newsroom" href="/newsroom" variant="footerAlt">
                      Newsroom
                    </CTALink>
                  </FooterSubHeroItems>
                  <FooterSubHeroItems>
                    <CTALink id="footer2contactus" href="mailto:hello@withjoy.com" variant="footerAlt">
                      Contact Us
                    </CTALink>
                  </FooterSubHeroItems>
                  <BottomMenuListMobile>{getFooterBottomMenu()}</BottomMenuListMobile>
                </div>
                <div className="footer-top-logo-wrapper">
                  <ParticleJoyLogo id="footer2brand-mobile" variant="primary" />
                </div>

                <SocialMediaMenuList>
                  <CTALink id="footer2instagram" href="https://www.instagram.com/withjoy" className="social-media-link" variant="footerAlt">
                    <JoykitIcon name="instagramSocial" color="linkFooterHover" />
                  </CTALink>
                  <CTALink
                    id="footer2pinterest"
                    href="https://www.pinterest.com/joytheapp"
                    className="social-media-link"
                    variant="footerAlt"
                  >
                    <JoykitIcon name="pinterest" color="linkFooterHover" />
                  </CTALink>
                  <CTALink id="footer2facebook" href="https://www.facebook.com/joytheapp" className="social-media-link" variant="footerAlt">
                    <JoykitIcon name="facebook" color="linkFooterHover" />
                  </CTALink>
                  <CTALink
                    id="footer2twitter"
                    href="https://twitter.com/joytheapp"
                    className="social-media-link twitter-link"
                    variant="footerAlt"
                  >
                    <JoykitIcon name="twitter" color="linkFooterHover" />
                  </CTALink>
                  <CTALinkHash
                    id="footer2twitter"
                    href="https://twitter.com/joytheapp"
                    className="social-media-link twitter-link"
                    variant="footerAlt"
                  >
                    #withjoy
                  </CTALinkHash>
                </SocialMediaMenuList>
                <CTALinkHashMobile id="footer2twitter" href="https://twitter.com/joytheapp" className="twitter-link" variant="footerAlt">
                  #withjoy
                </CTALinkHashMobile>
              </div>
            </Flex>
          </FooterColumn>
        </FooterColumns>
      </div>

      <div className="footer-bottom-menu">
        <Columns>
          <Columns.Column width={8}>
            <div className="footer-bottom-menu-left">{appGetStartedMenuContent({ page, handleGetStarted })}</div>
          </Columns.Column>
          <Columns.Column width={4}>
            <div className="footer-bottom-menu-right">
              <ParticleJoyLogo id="footer2brand-desktop" variant="primary" />
            </div>
          </Columns.Column>
        </Columns>
      </div>

      <BottomMenuList>{getFooterBottomMenu()}</BottomMenuList>

      {!isAcceptCookies && !isClosedCookies && showCookiesBar ? (
        <CookiesWrapper>
          <CookiesBanner onAction={() => setIsAcceptCookies(true)} onClose={() => closeWindow()} />
        </CookiesWrapper>
      ) : null}
    </footer>
  );
};
